import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  Check, Clock, Users, MapPin, CalendarDays, Gift, ArrowRight,
  MessageSquareText, FileText, Image, Sparkles,
} from 'lucide-react';
import { ContactForm } from '@/components/info/ContactForm';
import { fadeInUp, staggerContainer } from '@/lib/animations';
import { trackEvent } from '@/lib/capi';

const FORM_SECTION_ID = 'workshop-form';

/* Update these two lines once the session is confirmed. */
const WORKSHOP_DATE = 'Date TBC';
const WORKSHOP_TIME = 'Time TBC';

// Same ad-arrival test LandingPage uses: Meta appends `fbclid` to ad clicks.
const AD_SOURCE_PARAMS = ['facebook', 'instagram', 'meta', 'fb'];
function isFromAdClick() {
  const params = new URLSearchParams(window.location.search);
  return params.has('fbclid') || AD_SOURCE_PARAMS.includes((params.get('utm_source') || '').toLowerCase());
}

const INFO_CHIPS = [
  { icon: Gift, text: 'Free' },
  { icon: Clock, text: '90 minutes' },
  { icon: Users, text: 'Limited spots' },
  { icon: MapPin, text: 'Online or in person' },
  { icon: CalendarDays, text: `${WORKSHOP_DATE} · ${WORKSHOP_TIME}` },
];

const OUTCOMES = [
  { icon: MessageSquareText, title: 'Use AI with confidence', body: 'Know which tool to open, what to ask it, and when to trust the answer.' },
  { icon: Clock, title: 'Save time on everyday admin', body: 'Receipts into a spreadsheet, supplier quotes compared, meeting notes turned into actions.' },
  { icon: FileText, title: 'Draft documents and posts', body: 'Quotes, emails and social posts drafted in minutes, then checked by you.' },
  { icon: CalendarDays, title: 'Organise your day', body: 'Rosters, to-do lists and weekly plans sorted without the back and forth.' },
];

const COVERED = [
  'What AI is, and why it sometimes gets things confidently wrong',
  'How to write prompts that get a usable answer first go',
  'A live demo: handwritten job notes turned into a quote, an email and a checklist',
  'Real examples from local businesses using AI every week',
  "What's safe to put into AI, and what isn't",
  'Your questions, answered live',
];

const AUDIENCE = [
  "You've heard about ChatGPT but haven't really used it",
  "You've tried it and got rubbish answers",
  'Admin is eating your evenings',
  "You want to know what's worth your time and what's hype",
];

function scrollToForm(e) {
  e.preventDefault();
  trackEvent('CTAClick', { eventParams: { cta_text: 'Secure your spot', cta_location: 'hero' } });
  document.getElementById(FORM_SECTION_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/* Hero visual: a prompt going in and finished work coming out, with the tools floating around it. */
function WorkshopVisual() {
  const tiles = [
    { icon: MessageSquareText, pos: '-left-4 top-6', delay: 0 },
    { icon: FileText, pos: '-right-4 top-16', delay: 1.5 },
    { icon: Image, pos: '-left-6 bottom-20', delay: 3 },
    { icon: CalendarDays, pos: '-right-6 bottom-8', delay: 4.5 },
  ];
  const outputs = [
    { icon: FileText, label: 'Quote #1042', meta: '3 line items · $1,860' },
    { icon: MessageSquareText, label: 'Customer email', meta: 'Ready to send' },
    { icon: Check, label: 'Job checklist', meta: '6 steps' },
  ];

  return (
    <div aria-hidden="true" className="relative mx-auto w-full max-w-md px-6 py-6">
      <div className="absolute inset-10 rounded-full blur-3xl opacity-40" style={{ background: 'rgba(var(--c-purple-rgb),0.5)' }} />

      <div className="glass-card relative p-5 md:p-6 space-y-4">
        <div className="flex items-center gap-2 text-xs text-brand-muted">
          <span className="w-2 h-2 rounded-full bg-green-400" /> AI assistant
        </div>

        <div className="ml-auto max-w-[85%] rounded-2xl rounded-br-md px-4 py-3 text-sm text-white bg-brand-blue">
          Here are my job notes and price list. Write the quote, an email to the customer and a checklist for the job.
        </div>

        <div className="flex items-center gap-2 text-xs text-brand-blue-light">
          <Sparkles size={14} /> Done in 20 seconds
        </div>

        <div className="space-y-2">
          {outputs.map(({ icon: Icon, label, meta }, i) => (
            <motion.div
              key={label}
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 + i * 0.25, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="flex items-center gap-3 rounded-xl px-3 py-2.5"
              style={{ background: 'var(--brand-input-bg)', border: '1px solid var(--brand-glass-border)' }}
            >
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(var(--c-purple-rgb),0.15)' }}>
                <Icon size={16} className="text-brand-blue-light" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-brand-white">{label}</div>
                <div className="text-xs text-brand-muted">{meta}</div>
              </div>
              <Check size={16} className="text-green-400" />
            </motion.div>
          ))}
        </div>
      </div>

      {tiles.map(({ icon: Icon, pos, delay }) => (
        <div
          key={pos}
          className={`absolute ${pos} w-12 h-12 md:w-14 md:h-14 rounded-2xl flex items-center justify-center bg-brand-blue text-white animate-float shadow-[0_0_30px_rgba(var(--c-purple-rgb),0.55)]`}
          style={{ animationDelay: `${delay}s` }}
        >
          <Icon size={22} />
        </div>
      ))}
    </div>
  );
}

function HeroSection() {
  return (
    <section data-testid="workshop-hero" className="py-12 md:py-20 bg-brand-black">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid lg:grid-cols-12 gap-12 items-center">
        <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="lg:col-span-7">
          <motion.p variants={fadeInUp} className="subtitle">Free 90 min workshop</motion.p>
          <motion.h1 variants={fadeInUp} className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight text-brand-white mb-5">
            Learn how to make <span className="logo-gradient-text">AI</span> work for your <span className="logo-gradient-text">business.</span>
          </motion.h1>
          <motion.p variants={fadeInUp} className="text-base md:text-lg text-brand-muted max-w-xl leading-relaxed">
            Practical tips, real examples and simple tools you can use straight away.
          </motion.p>
          <motion.ul variants={fadeInUp} className="mt-8 flex flex-wrap gap-2 list-none p-0">
            {INFO_CHIPS.map(({ icon: Icon, text }) => (
              <li key={text} className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm text-brand-off-white" style={{ border: '1px solid var(--brand-border)' }}>
                <Icon size={15} className="text-brand-blue-light" /> {text}
              </li>
            ))}
          </motion.ul>
          <motion.div variants={fadeInUp} className="mt-10">
            <a
              href={`#${FORM_SECTION_ID}`}
              onClick={scrollToForm}
              data-testid="workshop-cta-hero"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-brand-blue text-white font-semibold transition-all duration-300 shadow-[0_0_20px_rgba(var(--c-purple-rgb),0.3)] hover:shadow-[0_0_40px_rgba(var(--c-purple-rgb),0.6)] hover:bg-brand-blue-light"
            >
              Secure your spot <ArrowRight size={18} />
            </a>
          </motion.div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5"
        >
          <WorkshopVisual />
        </motion.div>
      </div>
    </section>
  );
}

function OutcomesSection() {
  return (
    <section data-testid="workshop-outcomes" className="py-20 md:py-24 bg-brand-charcoal">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="text-center mb-12">
          <motion.p variants={fadeInUp} className="subtitle">In 90 minutes</motion.p>
          <motion.h2 variants={fadeInUp} className="title">What you&apos;ll <span>walk away with</span></motion.h2>
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {OUTCOMES.map(({ icon: Icon, title, body }) => (
            <motion.div key={title} variants={fadeInUp} className="glass-card glass-card-hover p-6 flex flex-col gap-3">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: 'rgba(var(--c-purple-rgb),0.1)' }}>
                <Icon size={20} className="text-brand-blue" />
              </div>
              <h3 className="text-base font-bold text-brand-white">{title}</h3>
              <p className="text-sm text-brand-muted leading-relaxed">{body}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function CheckList({ heading, items }) {
  return (
    <motion.div variants={fadeInUp} className="glass-card p-6 md:p-8">
      <h2 className="text-xl font-bold text-brand-white mb-5">{heading}</h2>
      <ul className="space-y-3 list-none p-0 m-0">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-3 text-sm md:text-base text-brand-off-white leading-relaxed">
            <Check size={18} className="text-brand-blue mt-0.5 flex-shrink-0" />
            {item}
          </li>
        ))}
      </ul>
    </motion.div>
  );
}

function DetailsSection() {
  return (
    <section data-testid="workshop-details" className="py-20 md:py-24 bg-brand-black">
      <motion.div
        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}
        className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 grid lg:grid-cols-2 gap-6"
      >
        <CheckList heading="What we'll cover" items={COVERED} />
        <CheckList heading="Who it's for" items={AUDIENCE} />
      </motion.div>
    </section>
  );
}

function FormSection() {
  return (
    <section id={FORM_SECTION_ID} data-testid="workshop-form-section" className="py-20 md:py-24 bg-brand-charcoal" style={{ scrollMarginTop: '4rem' }}>
      <div className="max-w-2xl mx-auto px-6 md:px-12 lg:px-16">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="text-center mb-10">
          <motion.p variants={fadeInUp} className="subtitle">Limited spots</motion.p>
          <motion.h2 variants={fadeInUp} className="title">Secure <span>your spot</span></motion.h2>
          <motion.p variants={fadeInUp} className="text-base text-brand-muted leading-relaxed">
            Pop your details in and we&apos;ll email you everything you need before the workshop.
          </motion.p>
        </motion.div>
        <ContactForm
          formName="training_workshop"
          showMessage={false}
          showSendAnother={false}
          submitLabel="Secure my spot"
          successTitle="You're booked in."
          successText="Keep an eye on your inbox. We'll send the details before the workshop."
          smallPrint="Free. No card needed. We'll only use your details for this workshop and related Custum AI updates."
        />
      </div>
    </section>
  );
}

export default function TrainingPage() {
  useEffect(() => {
    if (isFromAdClick()) {
      trackEvent('ViewContent', { eventParams: { content_name: 'training_workshop_page', content_category: 'paid_ad' } });
    }
  }, []);

  return (
    <div data-testid="training-page" className="pt-24 bg-brand-black overflow-x-clip">
      <Helmet>
        <title>Free AI Training Workshop — custum.ai</title>
        <meta name="description" content="A free 90 minute workshop on making AI work for your business. Practical tips, real examples and simple tools you can use straight away. Online or in person." />
        <meta property="og:title" content="Free AI Training Workshop — custum.ai" />
        <meta property="og:description" content="Learn how to make AI work for your business. Free 90 minute workshop, online or in person." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://custum.ai/training-workshop/" />
      </Helmet>
      <HeroSection />
      <OutcomesSection />
      <DetailsSection />
      <FormSection />
    </div>
  );
}
